import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Phone, PhoneCall, Search, Send, Bot, User, Headset, PhoneOff, Sparkles } from "lucide-react";
import { cn } from "@/lib/utils";
import { toast } from "sonner";
import { callStatusMeta, fmtDuration } from "@/config/voice";
import { fmtDateTime } from "@/config/crm";

function Simulator({ open, onOpenChange, onEnded }) {
  const [caller, setCaller] = useState("+91 90000 12345");
  const [call, setCall] = useState(null);
  const [turns, setTurns] = useState([]);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const scrollRef = useRef(null);

  const reset = () => { setCall(null); setTurns([]); setText(""); };

  const start = async () => {
    if (!caller.trim()) return;
    setBusy(true);
    try {
      const { data } = await api.post("/voice/simulate/start", { caller_number: caller.trim() });
      setCall(data);
      setTurns([{ speaker: "ai", text: data.greeting }]);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to start"); }
    finally { setBusy(false); }
  };

  const say = async () => {
    if (!text.trim() || !call || busy) return;
    const content = text.trim();
    setText("");
    setTurns((t) => [...t, { speaker: "caller", text: content }]);
    setBusy(true);
    try {
      const { data } = await api.post(`/voice/calls/${call.call_id}/turn`, { content });
      setTurns((t) => [...t, { speaker: "ai", text: data.reply, handoff: data.handoff }]);
      setTimeout(() => scrollRef.current?.scrollTo(0, scrollRef.current.scrollHeight), 60);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed"); }
    finally { setBusy(false); }
  };

  const end = async () => {
    if (!call) return;
    try { await api.post(`/voice/calls/${call.call_id}/end`); toast.success("Call ended & analyzed"); }
    catch { /* ignore */ }
    onOpenChange(false);
    reset();
    onEnded && onEnded();
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { onOpenChange(v); if (!v) reset(); }}>
      <DialogContent className="sm:max-w-lg" data-testid="voice-simulator-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 font-heading">
            <PhoneCall className="h-4 w-4 text-emerald-600" /> Voice AI Test Call
          </DialogTitle>
        </DialogHeader>
        {!call ? (
          <div className="space-y-3 py-2">
            <Label>Caller number</Label>
            <Input value={caller} onChange={(e) => setCaller(e.target.value)} data-testid="sim-caller-number" />
            <p className="text-xs text-slate-400">Simulates an inbound call through the same Wellora AI orchestrator (no telephony cost).</p>
            <Button onClick={start} disabled={busy} className="w-full bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="sim-start-call">
              <PhoneCall className="h-4 w-4" /> Start Simulated Call
            </Button>
          </div>
        ) : (
          <div className="space-y-3">
            <div ref={scrollRef} className="max-h-72 space-y-2 overflow-y-auto rounded-lg bg-slate-50/60 p-3">
              {turns.map((t, i) => (
                <div key={i} className={cn("flex", t.speaker === "caller" ? "justify-start" : "justify-end")} data-testid="sim-turn">
                  <div className={cn("max-w-[80%] rounded-lg px-3 py-2 text-sm",
                    t.speaker === "caller" ? "border border-slate-200 bg-white text-slate-800" : "bg-emerald-600 text-white")}>
                    <p className="mb-0.5 flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wider opacity-80">
                      {t.speaker === "caller" ? <><User className="h-3 w-3" /> Caller</> : <><Bot className="h-3 w-3" /> Wellora AI</>}
                    </p>
                    <p className="whitespace-pre-wrap">{t.text}</p>
                    {t.handoff && <p className="mt-1 flex items-center gap-1 text-[10px] text-amber-100"><Headset className="h-3 w-3" /> Transferring to staff</p>}
                  </div>
                </div>
              ))}
            </div>
            <div className="flex items-center gap-2">
              <Input placeholder="Speak as the caller… e.g. What is the laser skin treatment price?" value={text}
                onChange={(e) => setText(e.target.value)} onKeyDown={(e) => e.key === "Enter" && say()} data-testid="sim-say-input" />
              <Button onClick={say} disabled={busy} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="sim-say-button"><Send className="h-4 w-4" /></Button>
            </div>
          </div>
        )}
        <DialogFooter>
          {call && <Button variant="outline" onClick={end} data-testid="sim-end-call"><PhoneOff className="h-4 w-4" /> End Call</Button>}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function Calls() {
  const navigate = useNavigate();
  const [calls, setCalls] = useState([]);
  const [search, setSearch] = useState("");
  const [direction, setDirection] = useState("all");
  const [status, setStatus] = useState("all");
  const [simOpen, setSimOpen] = useState(false);

  const load = useCallback(async () => {
    const params = {};
    if (search.trim()) params.search = search.trim();
    if (direction !== "all") params.direction = direction;
    if (status !== "all") params.status = status;
    const { data } = await api.get("/voice/calls", { params });
    setCalls(data.calls);
  }, [search, direction, status]);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="animate-fade-up space-y-4" data-testid="calls-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-2xl font-semibold tracking-tight text-slate-900">Calls</h1>
          <p className="mt-1 text-sm text-slate-500">Voice AI channel · same orchestrator, knowledge & CRM as chat</p>
        </div>
        <Button onClick={() => setSimOpen(true)} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="test-call-button">
          <PhoneCall className="h-4 w-4" /> Test Voice Call
        </Button>
      </div>

      <Card className="shadow-sm">
        <div className="flex flex-col gap-3 border-b border-slate-100 p-4 sm:flex-row sm:items-center">
          <div className="relative flex-1"><Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
            <Input placeholder="Search caller number…" value={search} onChange={(e) => setSearch(e.target.value)} className="pl-9" data-testid="calls-search" /></div>
          <Select value={direction} onValueChange={setDirection}>
            <SelectTrigger className="w-40" data-testid="calls-direction-filter"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="all">All directions</SelectItem><SelectItem value="inbound">Inbound</SelectItem><SelectItem value="outbound">Outbound</SelectItem></SelectContent>
          </Select>
          <Select value={status} onValueChange={setStatus}>
            <SelectTrigger className="w-40" data-testid="calls-status-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All statuses</SelectItem>
              <SelectItem value="in_progress">In Progress</SelectItem>
              <SelectItem value="completed">Completed</SelectItem>
              <SelectItem value="transferred">Transferred</SelectItem>
              <SelectItem value="missed">Missed</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div className="overflow-x-auto">
          {calls.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 text-slate-400" data-testid="calls-empty">
              <Phone className="mb-2 h-8 w-8" /><p className="text-sm">No calls yet. Start a test call to see the AI voice flow.</p>
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead><tr className="border-b border-slate-100 text-left text-xs uppercase tracking-wider text-slate-400">
                <th className="px-4 py-3">Caller</th><th className="px-4 py-3">Direction</th><th className="px-4 py-3">Intent</th>
                <th className="px-4 py-3">Duration</th><th className="px-4 py-3">Outcome</th><th className="px-4 py-3">Assigned</th><th className="px-4 py-3">When</th>
              </tr></thead>
              <tbody>
                {calls.map((c) => (
                  <tr key={c.id} onClick={() => navigate(`/calls/${c.id}`)} className="cursor-pointer border-b border-slate-50 hover:bg-slate-50" data-testid="call-row">
                    <td className="px-4 py-3"><p className="font-medium text-slate-900">{c.contact_name}</p><p className="font-mono text-xs text-slate-400">{c.caller_number}</p></td>
                    <td className="px-4 py-3 capitalize text-slate-600">{c.direction}</td>
                    <td className="px-4 py-3 capitalize text-slate-600">{(c.intent || "—").replace(/_/g, " ")}{c.handoff_requested && <Headset className="ml-1 inline h-3.5 w-3.5 text-amber-500" />}</td>
                    <td className="px-4 py-3 text-slate-600">{fmtDuration(c.duration)}</td>
                    <td className="px-4 py-3"><Badge className={cn(callStatusMeta(c.status).badge)}>{callStatusMeta(c.status).label}</Badge></td>
                    <td className="px-4 py-3 text-slate-600">{c.owner_name || "—"}</td>
                    <td className="px-4 py-3 text-xs text-slate-400">{fmtDateTime(c.start_time)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </Card>

      <p className="flex items-center gap-1 text-xs text-slate-400"><Sparkles className="h-3.5 w-3.5 text-emerald-500" /> Configure a real telephony provider in Settings, or use Test mode with zero external cost.</p>

      <Simulator open={simOpen} onOpenChange={setSimOpen} onEnded={load} />
    </div>
  );
}
